import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import Collapse from '@mui/material/Collapse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FastForwardIcon from '@mui/icons-material/FastForward';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SensorsIcon from '@mui/icons-material/Sensors';
import api, { ApiError } from '../api';
import LineBadge from './LineBadge';
import BusScheduleContent from './BusScheduleContent';

const pulse = {
  '@keyframes pulse': {
    '0%,100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(102,187,106,0.5)' },
    '50%': { opacity: 0.6, boxShadow: '0 0 0 6px rgba(102,187,106,0)' },
  },
};

const blink = {
  '@keyframes blink': {
    '0%,100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
};

const countdownPulse = {
  '@keyframes countdownPulse': {
    '0%,100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' },
  },
};

/* ───────── Direction Picker ───────── */

function DirectionPicker({ schedule, onSelect, t }) {
  const directions = useMemo(() => {
    return (schedule?.directions || []).map((d) => {
      const sched = schedule?.schedules?.find((s) => s.key === d.key);
      const firstBus = sched?.next_buses?.[0];
      return {
        lineName: d.line,
        direction: d.direction,
        directionKey: d.key,
        color: d.color || sched?.color || '#667eea',
        textColor: d.textColor || sched?.text_color || '#fff',
        nextMinutes: firstBus?.minutes_away ?? null,
      };
    });
  }, [schedule]);

  return (
    <Box sx={{ minHeight: '100vh', color: '#fff', maxWidth: { sm: 480 }, mx: 'auto' }}>
      <Stack alignItems="center" sx={{ pt: 5, pb: 3, px: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 48, mb: 2 }}>🚏</Typography>
        <Typography fontWeight={600} fontSize={15} color="rgba(255,255,255,0.85)" sx={{ mb: 0.5 }}>
          {schedule?.stop_name}
        </Typography>
        <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>
          {t('directionPicker.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 320 }}>
          {t('directionPicker.description')}
        </Typography>
      </Stack>

      <Stack spacing={1.5} sx={{ px: 2, pb: 4 }}>
        {directions.map((d) => (
          <Card
            key={`${d.lineName}__${d.direction}__${d.directionKey ?? ''}`}
            elevation={3}
            sx={{
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': { borderColor: 'primary.main', boxShadow: '0 0 20px rgba(79,195,247,0.15)' },
            }}
          >
            <CardActionArea
              onClick={() => onSelect({ key: d.directionKey, direction: d.direction })}
              sx={{ p: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip
                  label={d.lineName}
                  sx={{
                    bgcolor: d.color || '#667eea',
                    color: d.textColor || '#fff',
                    fontWeight: 900,
                    fontSize: 14,
                    height: 44,
                    minWidth: 44,
                    borderRadius: '12px',
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontSize={12} fontWeight={700} color="rgba(255,255,255,0.5)" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                    {t('schedule.line', { name: d.lineName })}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <DirectionsBusIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
                    <Typography fontWeight={700} fontSize={15} color="#fff" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.direction}
                    </Typography>
                  </Stack>
                  {d.nextMinutes != null && (
                    <Typography fontSize={12} color="success.main" fontWeight={600} sx={{ mt: 0.5 }}>
                      {t('directionPicker.nextBusIn', { minutes: d.nextMinutes })}
                    </Typography>
                  )}
                </Box>
                <ArrowForwardIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 22 }} />
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

    </Box>
  );
}

/* ───────── Badges ───────── */

function SourceBadge({ isRealtime, label }) {
  if (isRealtime) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#2e7d32' }}>
        <SensorsIcon sx={{ fontSize: 20, ...pulse, animation: 'pulse 2s ease-in-out infinite' }} />
      </Box>
    );
  }
  return (
    <Chip
      size="small"
      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
      label={label}
      sx={{ bgcolor: '#e8eaf6', color: '#5c6bc0', fontWeight: 700, fontSize: 11 }}
    />
  );
}

function StatusBadge({ minutesAway, isRealtime, isDelayed, status, t }) {
  if (!isRealtime) return null;
  if (isDelayed) {
    return (
      <Chip
        icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
        label={t('schedule.delayed')}
        size="small"
        sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 800 }}
      />
    );
  }
  // Running ahead of schedule: surface this prominently so the rider
  // doesn't miss the bus by showing up at the printed time. Kept on the
  // same visual family as "delayed" (warning-styled) but in blue to stay
  // distinct from the amber of "late".
  if (status === 'early') {
    return (
      <Chip
        icon={<FastForwardIcon sx={{ fontSize: 14 }} />}
        label={t('schedule.early')}
        size="small"
        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 800 }}
      />
    );
  }
  if (minutesAway <= 1) {
    return (
      <Chip
        icon={<DirectionsBusIcon sx={{ fontSize: 16 }} />}
        label={t('schedule.atPlatform')}
        size="small"
        sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 800, ...blink, animation: 'blink 1s ease-in-out infinite' }}
      />
    );
  }
  // Show "À L'HEURE" both when the backend has confirmed the trip is
  // on time *and* when the trip is tracked live but isn't in our static
  // schedule (``status === 'unknown'``). The latter happens for trips
  // the feed is publishing that haven't been imported into our DB — we
  // still have a live prediction, just no scheduled baseline to diff
  // against. Treating it as "on time" is the same pragmatic default
  // most GTFS consumers use; the schedule-join logic above already
  // surfaces the real ``delayed``/``early`` status whenever we can
  // actually compute it.
  //
  // We no longer gate this on ``minutesAway <= 10``. Now that we render
  // every RT passage (not just the imminent one), a 30-min-away live
  // bus deserves the same visual confirmation as a 5-min-away one —
  // otherwise neighboring cards for the same line would inconsistently
  // show / hide the badge purely based on how far out they are.
  if (status === 'on_time' || status === 'unknown') {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
        label={t('schedule.onTime')}
        size="small"
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}
      />
    );
  }
  return null;
}

/* ───────── Arrival Card ───────── */

function ArrivalCard({ arrival, isNext, getCountdown, t }) {
  const [showNextStops, setShowNextStops] = useState(false);
  const countdown = getCountdown(arrival.minutesAway);
  const showCountdown = isNext && arrival.isRealtime;
  const hasNextStops = (arrival.nextStops?.length ?? 0) > 0;

  // "Shifted" means the live time differs from the scheduled time in a
  // way the rider needs to notice — either late (red) or early (blue).
  // We show both the struck-through scheduled time and the adjusted RT
  // time so the rider can tell that their original plan is off.
  const isEarly = arrival.status === 'early';
  const isShifted =
    (arrival.isDelayed || isEarly) &&
    arrival.scheduledArrivalTime &&
    arrival.scheduledArrivalTime !== arrival.arrivalTime;
  const shiftedColor = arrival.isDelayed ? '#e53935' : '#1565c0';

  return (
    <Card
      elevation={showCountdown ? 6 : 2}
      sx={{
        bgcolor: 'rgba(255,255,255,0.97)',
        color: '#1a1a2e',
        overflow: 'hidden',
        opacity: arrival.isRealtime ? 1 : 0.85,
        border: showCountdown ? '2px solid' : 'none',
        borderColor: showCountdown ? 'success.main' : 'transparent',
        boxShadow: showCountdown ? '0 4px 20px rgba(102,187,106,0.3)' : undefined,
        transition: 'transform 0.2s',
        '&:active': { transform: 'scale(0.985)' },
      }}
    >
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75} sx={{ px: 1.75, pt: 1.75, pb: 1 }}>
        {isShifted ? (
          <Stack direction="row" alignItems="baseline" gap={0.75}>
            <Typography fontWeight={600} fontSize={14} sx={{ fontVariantNumeric: 'tabular-nums', color: '#999', textDecoration: 'line-through' }}>
              {arrival.scheduledArrivalTime}
            </Typography>
            <Typography fontWeight={800} fontSize={20} sx={{ fontVariantNumeric: 'tabular-nums', color: shiftedColor }}>
              {arrival.arrivalTime}
            </Typography>
          </Stack>
        ) : (
          <Typography fontWeight={800} fontSize={20} sx={{ fontVariantNumeric: 'tabular-nums', color: '#0a1628' }}>
            {arrival.arrivalTime}
          </Typography>
        )}
        <Typography color="#999" fontWeight={300}>–</Typography>
        <Typography fontWeight={700} fontSize={13} color="#333" sx={{ textTransform: 'uppercase', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {arrival.direction}
        </Typography>
        <LineBadge
          lineName={arrival.lineName}
          color={arrival.color}
          textColor={arrival.textColor}
          labelPrefix={t('schedule.line', { name: '' }).trim()}
        />
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.75, py: 0.75, borderTop: '1px solid #f0f0f0' }}>
        <SourceBadge isRealtime={arrival.isRealtime} label={t('schedule.scheduledTime')} />
        <StatusBadge minutesAway={arrival.minutesAway} isRealtime={arrival.isRealtime} isDelayed={arrival.isDelayed} status={arrival.status} t={t} />
      </Stack>

      {hasNextStops && (
        <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
          <Button
            fullWidth
            onClick={() => setShowNextStops((open) => !open)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showNextStops ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                  fontSize: 22,
                }}
              />
            }
            sx={{
              justifyContent: 'space-between',
              px: 1.75,
              py: 1,
              color: '#5c6bc0',
              fontWeight: 700,
              fontSize: 13,
              textTransform: 'none',
            }}
          >
            {t('schedule.nextStopsToggle')}
          </Button>
          <Collapse in={showNextStops}>
            <Box sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}>
              <Box
                sx={{
                  px: 1.25,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: '#f0f4ff',
                  border: '1px solid #e0e7ff',
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ flexWrap: 'nowrap', minWidth: 0, justifyContent: 'center' }}
                >
                  {arrival.nextStops.map((name, idx) => (
                    <React.Fragment key={`${name}-${idx}`}>
                      {idx > 0 && (
                        <ArrowForwardIcon
                          sx={{
                            fontSize: 20,
                            color: '#7986cb',
                            flexShrink: 0,
                            mx: 0.25,
                          }}
                          aria-hidden
                        />
                      )}
                      <Box
                        sx={{
                          flexShrink: 0,
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 2,
                          bgcolor: '#fff',
                          border: '1px solid #c5cae9',
                          boxShadow: '0 1px 3px rgba(63,81,181,0.12)',
                          maxWidth: 140,
                        }}
                      >
                        <Typography
                          fontSize={13}
                          fontWeight={700}
                          color="#1a237e"
                          sx={{
                            lineHeight: 1.35,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}

      {showCountdown && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 1.75, py: 1.25, background: arrival.isDelayed ? 'linear-gradient(135deg, #e65100, #bf360c)' : 'linear-gradient(135deg, #1b5e20, #2e7d32)', color: '#fff' }}
        >
          <Typography fontSize={12} fontWeight={600} sx={{ opacity: 0.9 }}>
            {t('schedule.nextIn')}
          </Typography>
          <Typography fontWeight={900} fontSize={24} sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: 2, ...countdownPulse, animation: 'countdownPulse 1s ease-in-out infinite' }}>
            {countdown.mins}:{String(countdown.secs).padStart(2, '0')}
          </Typography>
        </Stack>
      )}
    </Card>
  );
}

/* ───────── Main Component ───────── */

function BusSchedule({ stopId, gtfsStopId = null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  /** Clé de direction sélectionnée (filtrage client-side) */
  const [directionKeyFilter, setDirectionKeyFilter] = useState(null);
  const [directionConfirmed, setDirectionConfirmed] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setDirectionKeyFilter(null);
    setDirectionConfirmed(false);
    setSchedule(null);
    setLoading(true);
  }, [stopId, gtfsStopId]);

  const fetchSchedule = useCallback(async () => {
    try {
      const data = await api.getSchedule(stopId, gtfsStopId);
      setSchedule(data);
      setError(null);
    } catch (err) {
      // The scanned GTFS stop ID no longer exists for this stop (e.g. the GTFS
      // data was re-imported and IDs changed).  Fall back to the plain stop
      // URL so the rider still sees all available schedules instead of an error.
      if (gtfsStopId && err instanceof ApiError && String(err.data?.error ?? '').includes('no_gtfs_ids')) {
        navigate(`/stop/${stopId}`, { replace: true });
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [stopId, gtfsStopId, navigate]);

  useEffect(() => {
    fetchSchedule();
    pollRef.current = setInterval(fetchSchedule, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchSchedule]);

  const uniqueDirections = useMemo(() => {
    if (!schedule?.schedules) return [];
    const seen = new Set();
    return schedule.schedules.filter((s) => {
      const key = `${s.line_name}__${s.direction}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [schedule]);

  const hasMultipleDirections = useMemo(() => {
    if (schedule?.directions?.length) return schedule.directions.length > 1;
    return uniqueDirections.length > 1;
  }, [schedule, uniqueDirections]);

  useEffect(() => {
    if (!schedule) return;
    if (schedule.directions?.length === 1) {
      setDirectionConfirmed(true);
    } else if (!schedule.directions?.length && uniqueDirections.length === 1) {
      setDirectionConfirmed(true);
    }
  }, [schedule, uniqueDirections]);

  const handleDirectionSelect = (payload) => {
    setDirectionKeyFilter(payload?.key ?? null);
    setDirectionConfirmed(true);
  };

  const getCountdown = (minutesAway) => {
    if (!schedule?.current_time) return { mins: minutesAway, secs: 0 };
    const fetchTime = new Date(schedule.current_time);
    const elapsed = Math.floor((currentTime - fetchTime) / 1000);
    const remaining = Math.max(0, minutesAway * 60 - elapsed);
    return { mins: Math.floor(remaining / 60), secs: remaining % 60 };
  };

  const getDirectionTabs = () =>
    (schedule?.directions || []).map((d) => ({
      key: d.key,
      label: `${d.line} → ${d.direction}`,
    }));

  const lineStyles = useMemo(() => {
    const map = new Map();
    (schedule?.schedules || []).forEach((s) => {
      if (!s?.line_name) return;
      const key = String(s.line_name).toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          name: s.line_name,
          color: s.color,
          textColor: s.text_color,
        });
      }
    });
    return Array.from(map.values());
  }, [schedule]);

  const getArrivals = () => {
    if (!schedule?.schedules) return [];
    const filtered = directionKeyFilter
      ? schedule.schedules.filter((s) => s.key === directionKeyFilter)
      : schedule.schedules;

    const all = [];
    filtered.forEach((sched) => {
      (sched.next_buses || []).forEach((bus) => {
        const isRealtime = bus.is_realtime ?? false;
        const scheduledTime = bus.scheduled_arrival_time || bus.arrival_time;
        const isDelayed = isRealtime && bus.status === 'delayed';
        all.push({
          arrivalTime: bus.arrival_time,
          scheduledArrivalTime: scheduledTime,
          minutesAway: bus.minutes_away,
          isRealtime,
          isDelayed,
          status: bus.status,
          lineName: sched.line_name,
          direction: sched.direction,
          color: sched.color,
          textColor: sched.text_color,
          category: sched.category,
          nextStops: sched.next_stops || [],
        });
      });
    });

    // Drop past buses and sort. We sort *and* compare by ``arrivalTime``
    // (HH:MM string) rather than ``minutesAway`` because the former comes
    // from the backend in Europe/Paris (DST-correct) whereas the latter is
    // partly computed in the browser, which may be misconfigured (e.g.
    // stuck at UTC+1 without DST). String comparison on ``HH:MM`` is
    // timezone-proof: 14:07 < 15:12 regardless of local offset.
    const timeKey = (a) => a.arrivalTime || a.scheduledArrivalTime || '';
    const upcoming = all
      .filter((a) => (a.minutesAway ?? 0) >= 0)
      .sort((a, b) => {
        const k = timeKey(a).localeCompare(timeKey(b));
        return k !== 0 ? k : (a.minutesAway - b.minutesAway);
      });

    // Display policy: RT passages are straight from the vehicle tracker
    // and always more trustworthy than static scheduled times, so we
    // show every RT passage we have (including "early" / "delayed" /
    // "unknown") before adding any theoretical fill. We then top up
    // with theoretical entries whose time is strictly after the last RT
    // one so the rider still gets a view of the next ~4 buses even
    // when only one or two have live tracking.
    const TOTAL_SLOTS = 4;
    const realtimeAll = upcoming.filter((a) => a.isRealtime);
    const lastRealtimeTime = realtimeAll.length
      ? timeKey(realtimeAll[realtimeAll.length - 1])
      : '';

    const seenTimes = new Set(realtimeAll.map(timeKey));
    const theoreticalFill = upcoming
      .filter((a) => !a.isRealtime)
      // Keep only theoretical passages that extend *beyond* what RT
      // already covers — they act as a fallback for trips the feed
      // hasn't started tracking yet.
      .filter((a) => !lastRealtimeTime || timeKey(a) > lastRealtimeTime)
      .filter((a) => {
        // Collapse duplicate theoretical entries that share a time
        // (e.g. two line variants both emitting 15:12).
        const k = timeKey(a);
        if (seenTimes.has(k)) return false;
        seenTimes.add(k);
        return true;
      });

    if (realtimeAll.length === 0) {
      return upcoming.slice(0, TOTAL_SLOTS);
    }

    const slotsLeft = Math.max(0, TOTAL_SLOTS - realtimeAll.length);
    return [...realtimeAll, ...theoreticalFill.slice(0, slotsLeft)];
  };

  /* ── Loading ── */
  if (loading && !schedule) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography color="text.secondary">{t('schedule.loading')}</Typography>
      </Box>
    );
  }

  /* ── Error ── */
  if (error && !schedule) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3 }}>
        <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />
        <Typography variant="h6" color="#fff">{t('schedule.connectionError')}</Typography>
        <Typography color="text.secondary" fontSize={14}>{error}</Typography>
        <Button variant="contained" onClick={fetchSchedule} sx={{ mt: 1, borderRadius: 25, px: 4 }}>
          {t('schedule.retry')}
        </Button>
      </Box>
    );
  }

  /* ── Direction picker (multi-line stops only) ── */
  if (hasMultipleDirections && !directionConfirmed) {
    return <DirectionPicker schedule={schedule} onSelect={handleDirectionSelect} t={t} />;
  }

  /* ── Schedule view ── */
  const directionTabs = getDirectionTabs();
  const arrivals = getArrivals();

  return (
    <Box sx={{ minHeight: '100vh', color: '#fff', pb: 'calc(80px + env(safe-area-inset-bottom, 0px))', maxWidth: { sm: 480 }, mx: 'auto' }}>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography fontWeight={800} fontSize={32} sx={{ letterSpacing: 2, fontVariantNumeric: 'tabular-nums' }}>
            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            setLoading(true);
            fetchSchedule();
          }}
          aria-label={t('schedule.refresh')}
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography fontWeight={800} fontSize={17} letterSpacing={1.5} color="secondary.main" sx={{ mb: 1 }}>
          {t('schedule.title')}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={600} fontSize={15} color="rgba(255,255,255,0.85)">
            {schedule?.stop_name}
          </Typography>
          {schedule?.is_realtime_global ? (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', ...pulse, animation: 'pulse 2s ease-in-out infinite' }} />
              <Typography fontSize={12} fontWeight={700} color="success.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('schedule.realtime')}
              </Typography>
            </Stack>
          ) : (
            <Typography fontSize={12} fontWeight={600} color="rgba(255,255,255,0.45)" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('schedule.scheduledTime')}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Direction carousel — only for multi-line stops */}
      {hasMultipleDirections && (
        <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.75, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
          <Chip
            label={t('schedule.allDirections')}
            variant={!directionKeyFilter ? 'filled' : 'outlined'}
            onClick={() => setDirectionKeyFilter(null)}
            sx={{
              bgcolor: !directionKeyFilter ? 'rgba(79,195,247,0.2)' : 'rgba(255,255,255,0.08)',
              borderColor: !directionKeyFilter ? 'primary.main' : 'rgba(255,255,255,0.15)',
              color: !directionKeyFilter ? 'primary.main' : 'rgba(255,255,255,0.75)',
            }}
          />
          {directionTabs.map((d) => {
            const active = directionKeyFilter === d.key;
            return (
              <Chip
                key={d.key}
                label={d.label}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setDirectionKeyFilter(d.key)}
                sx={{
                  flexShrink: 0,
                  bgcolor: active ? 'rgba(79,195,247,0.2)' : 'rgba(255,255,255,0.08)',
                  borderColor: active ? 'primary.main' : 'rgba(255,255,255,0.15)',
                  color: active ? 'primary.main' : 'rgba(255,255,255,0.75)',
                }}
              />
            );
          })}
        </Stack>
      )}

      <BusScheduleContent
        error={error}
        schedule={schedule}
        fetchSchedule={fetchSchedule}
        loading={loading}
        arrivals={arrivals}
        ArrivalCard={ArrivalCard}
        getCountdown={getCountdown}
        t={t}
        currentTime={currentTime}
        lineStyles={lineStyles}
      />
    </Box>
  );
}

export default BusSchedule;